import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Globe, BarChart3, Settings2, Bot,
  Clock, FolderKanban, Eye, Zap, LayoutGrid,
  ArrowRight, ArrowUpRight, ChevronRight
} from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.5 }
};

const stagger = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const benefits = [
  { icon: FolderKanban, title: "Structurer votre activité", desc: "Organisez vos processus pour gagner en efficacité et en clarté." },
  { icon: Clock, title: "Gagner du temps", desc: "Automatisez les tâches répétitives et concentrez-vous sur votre métier." },
  { icon: LayoutGrid, title: "Améliorer votre organisation", desc: "Des outils adaptés pour piloter votre activité au quotidien." },
  { icon: Eye, title: "Développer votre visibilité", desc: "Un site professionnel et une présence en ligne qui attirent vos clients." },
  { icon: Zap, title: "Automatiser les tâches", desc: "Des flux automatisés pour réduire les erreurs et accélérer votre travail." },
];

const services = [
  {
    icon: Globe,
    title: "Création de site internet professionnel",
    desc: "Des sites conçus pour attirer vos clients, présenter votre expertise et générer des demandes.",
    link: "/services"
  },
  {
    icon: BarChart3,
    title: "Diagnostic stratégique",
    desc: "Analyse structurée de votre organisation, vos outils et vos opportunités de croissance.",
    link: "/services"
  },
  {
    icon: Settings2,
    title: "Outils de gestion",
    desc: "CRM, automatisations, tableaux de bord : les outils pour piloter votre activité.",
    link: "/services"
  },
  {
    icon: Bot,
    title: "Automatisations & IA",
    desc: "Assistants IA, workflows automatisés et solutions sur mesure pour gagner en productivité.",
    link: "/services"
  },
];

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background elements */}
        <div className="absolute inset-0 bg-[#0B1F3B]">
          <div className="absolute top-1/4 -left-32 w-96 h-96 bg-[#59C3A6]/5 rounded-full blur-3xl animate-pulse-glow" />
          <div className="absolute bottom-1/4 -right-32 w-[500px] h-[500px] bg-[#112A4F]/60 rounded-full blur-3xl" />
          {/* Geometric lines */}
          <svg className="absolute inset-0 w-full h-full opacity-[0.03]" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse">
                <path d="M 60 0 L 0 0 0 60" fill="none" stroke="white" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 text-center pt-24">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#59C3A6]/10 border border-[#59C3A6]/20 mb-8"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#59C3A6] animate-pulse" />
            <span className="text-[#59C3A6] text-sm font-medium">Expertise & Innovation</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight mb-6"
          >
            Donnez une longueur d'avance{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#59C3A6] to-[#4AB095]">
              à votre métier.
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-base md:text-lg text-slate-300 max-w-3xl mx-auto leading-relaxed mb-10"
          >
            Finovéo Conseil accompagne les artisans et petites entreprises dans la structuration, la digitalisation et l'optimisation de leur activité grâce aux outils numériques, aux automatisations et à l'intelligence artificielle.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link to="/diagnostic">
              <Button
                data-testid="hero-cta-primary"
                className="bg-[#59C3A6] text-[#0B1F3B] hover:bg-[#4AB095] font-semibold px-8 py-3 h-auto rounded-full transition-all duration-300 hover:scale-105 shadow-[0_0_30px_rgba(89,195,166,0.25)] text-base"
              >
                Demander mon diagnostic gratuit
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
            <Link to="/services">
              <Button
                data-testid="hero-cta-secondary"
                variant="outline"
                className="border-[#59C3A6]/30 text-[#59C3A6] hover:bg-[#59C3A6]/10 hover:text-[#59C3A6] px-8 py-3 h-auto rounded-full transition-all duration-300 text-base bg-transparent"
              >
                Découvrir nos solutions
                <ChevronRight className="ml-1" size={18} />
              </Button>
            </Link>
          </motion.div>

          {/* Trust indicators */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-16 flex items-center justify-center gap-8 text-slate-500 text-sm"
          >
            <span className="flex items-center gap-2"><Zap size={14} className="text-[#59C3A6]" /> Automatisation</span>
            <span className="hidden sm:block w-px h-4 bg-slate-700" />
            <span className="flex items-center gap-2"><BarChart3 size={14} className="text-[#59C3A6]" /> Croissance</span>
            <span className="hidden sm:block w-px h-4 bg-slate-700" />
            <span className="flex items-center gap-2"><Settings2 size={14} className="text-[#59C3A6]" /> Organisation</span>
          </motion.div>
        </div>
      </section>

      {/* Value Proposition */}
      <section data-testid="value-section" className="py-20 md:py-32 px-6 md:px-12 lg:px-16 bg-[#0B1F3B]">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeUp} className="max-w-2xl mb-16">
            <span className="text-[#59C3A6] text-sm font-semibold uppercase tracking-wider">Notre valeur</span>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tight mt-3 mb-5 text-white">
              Le partenaire digital des entrepreneurs et artisans
            </h2>
            <p className="text-slate-300 text-base md:text-lg leading-relaxed">
              Finovéo aide les petites entreprises à accéder aux outils et stratégies habituellement réservés aux grandes structures.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
            {benefits.map((item, i) => (
              <motion.div
                key={i}
                {...stagger}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="group p-6 rounded-2xl bg-[#112A4F]/30 border border-white/5 hover:border-[#59C3A6]/20 transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-xl bg-[#59C3A6]/10 flex items-center justify-center mb-4 group-hover:bg-[#59C3A6]/20 transition-colors">
                  <item.icon size={20} className="text-[#59C3A6]" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section data-testid="services-overview" className="py-20 md:py-32 px-6 md:px-12 lg:px-16 bg-[#071428]">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-[#59C3A6] text-sm font-semibold uppercase tracking-wider">Nos services</span>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tight mt-3 mb-5 text-white">
              Des solutions concrètes pour votre croissance
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service, i) => (
              <motion.div
                key={i}
                {...stagger}
                transition={{ duration: 0.4, delay: i * 0.1 }}
              >
                <Link
                  to={service.link}
                  data-testid={`service-card-${i}`}
                  className="block group p-8 rounded-2xl bg-[#112A4F]/40 backdrop-blur-md border border-[#59C3A6]/10 hover:border-[#59C3A6]/30 transition-all duration-300 h-full relative overflow-hidden"
                >
                  <div className="absolute top-0 right-0 w-40 h-40 bg-[#59C3A6]/5 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 -translate-y-10 translate-x-10" />
                  <div className="relative z-10">
                    <div className="w-12 h-12 rounded-xl bg-[#59C3A6]/10 flex items-center justify-center mb-5 group-hover:bg-[#59C3A6]/20 transition-colors">
                      <service.icon size={24} className="text-[#59C3A6]" />
                    </div>
                    <h3 className="font-heading text-xl font-semibold text-white mb-3">{service.title}</h3>
                    <p className="text-slate-400 text-sm leading-relaxed mb-4">{service.desc}</p>
                    <span className="inline-flex items-center text-[#59C3A6] text-sm font-medium group-hover:gap-2 gap-1 transition-all">
                      En savoir plus <ArrowUpRight size={14} />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-20 md:py-32 px-6 md:px-12 lg:px-16 bg-[#0B1F3B] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#59C3A6]/5 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tight mb-5 text-white">
              Prêt à transformer votre activité ?
            </h2>
            <p className="text-slate-300 text-base md:text-lg mb-8 leading-relaxed">
              Nous analysons gratuitement votre organisation et vos outils pour identifier les leviers de croissance les plus impactants.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/diagnostic">
                <Button
                  data-testid="cta-diagnostic"
                  className="bg-[#59C3A6] text-[#0B1F3B] hover:bg-[#4AB095] font-semibold px-8 py-3 h-auto rounded-full transition-all duration-300 hover:scale-105 shadow-[0_0_30px_rgba(89,195,166,0.25)] text-base"
                >
                  Demander mon diagnostic gratuit
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
              <a href="https://calendly.com/finoveo-conseil/30min" target="_blank" rel="noopener noreferrer">
                <Button
                  data-testid="cta-calendly"
                  variant="outline"
                  className="border-[#59C3A6]/30 text-[#59C3A6] hover:bg-[#59C3A6]/10 hover:text-[#59C3A6] px-8 py-3 h-auto rounded-full transition-all duration-300 text-base bg-transparent"
                >
                  Réserver un appel
                  <ArrowUpRight className="ml-1" size={16} />
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
